import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tasksAPI, ordersAPI, customersAPI, materialsAPI, invoicesAPI, settingsAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Skeleton } from '../../components/ui/skeleton';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, CheckSquare, Calendar as CalendarIcon, Clock, ChevronsUpDown, Check, X, Phone, Mail, MapPin, Eye, Download, Smartphone, Copy, ExternalLink, RefreshCw, Package, FileText, ChevronDown } from 'lucide-react';
import { formatDate, formatDateTime, formatCurrency, TASK_PRIORITIES, TASK_STATUSES, ORDER_STATUSES, cn } from '../../lib/utils';
import PaywallDialog from '../../components/PaywallDialog';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { SocialIcon } from '../Customers/CustomersPage';

// Helper: download blob as file
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Social icons component for task cards
function CustomerSocialIcons({ customer }) {
  if (!customer) return null;
  const socialLinks = customer.social_links || {};
  const hasSocials = Object.values(socialLinks).some(v => v);
  const hasPhone = customer.phone;
  
  if (!hasSocials && !hasPhone) return null;

  return (
    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      {hasPhone && (
        <a 
          href={`tel:${customer.phone.replace(/\D/g, '')}`}
          className="p-1 rounded-full hover:bg-green-500/10 text-green-600 transition-colors"
          title={`Позвонить: ${customer.phone}`}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        </a>
      )}
      {socialLinks.telegram && (
        <a 
          href={`https://t.me/${socialLinks.telegram.replace('@', '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#0088cc]/10 text-[#0088cc] transition-colors"
          title="Telegram"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        </a>
      )}
      {socialLinks.whatsapp && (
        <a 
          href={`https://wa.me/${socialLinks.whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#25D366]/10 text-[#25D366] transition-colors"
          title="WhatsApp"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}
      {socialLinks.instagram && (
        <a 
          href={`https://instagram.com/${socialLinks.instagram.replace('@', '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#E4405F]/10 text-[#E4405F] transition-colors"
          title="Instagram"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
          </svg>
        </a>
      )}
      {socialLinks.vk && (
        <a 
          href={`https://vk.com/${socialLinks.vk}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded-full hover:bg-[#0077FF]/10 text-[#0077FF] transition-colors"
          title="ВКонтакте"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.391 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.12-5.339-3.202-2.17-3.042-2.763-5.32-2.763-5.785 0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.27-1.422 2.18-3.61 2.18-3.61.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
          </svg>
        </a>
      )}
    </div>
  );
}

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortByDate, setSortByDate] = useState('none');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order_id: '',
    material_id: '',
    invoice_id: '',
    due_date: null,
    due_time: '',
    notify: true,
    priority: 'medium',
    status: 'pending',
  });
  const [saving, setSaving] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('active'); // 'all' | 'active'
  const [orderSelectOpen, setOrderSelectOpen] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialSelectOpen, setMaterialSelectOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoiceSelectOpen, setInvoiceSelectOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [reminderMinutes, setReminderMinutes] = useState(30);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const orderPanelRef = useRef(null);
  const materialPanelRef = useRef(null);
  const invoicePanelRef = useRef(null);

  useEffect(() => {
    if (orderSelectOpen && orderPanelRef.current) {
      setTimeout(() => orderPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [orderSelectOpen]);

  useEffect(() => {
    if (materialSelectOpen && materialPanelRef.current) {
      setTimeout(() => materialPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [materialSelectOpen]);

  useEffect(() => {
    if (invoiceSelectOpen && invoicePanelRef.current) {
      setTimeout(() => invoicePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [invoiceSelectOpen]);

  // Helper function to get client name from order
  const getClientNameFromOrder = (order) => {
    if (!order) return '';
    // First try to get from customer card
    if (order.customer_id) {
      const customer = customers.find(c => c.id === order.customer_id);
      if (customer?.name) return customer.name;
    }
    // Fallback to order data
    if (order.data) {
      const nameFields = ['name', 'client_name', 'customer_name', 'имя', 'клиент', 'ФИО'];
      for (const field of nameFields) {
        if (order.data[field]) return order.data[field];
      }
    }
    return '';
  };

  // Filter orders based on search and status
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Status filter
      if (orderStatusFilter === 'active' && !['new', 'in_progress'].includes(order.status)) {
        return false;
      }
      
      // Search filter
      if (orderSearch) {
        const searchLower = orderSearch.toLowerCase();
        const clientName = getClientNameFromOrder(order).toLowerCase();
        const orderNum = (order.order_number_display || `№${order.order_number}`).toLowerCase();
        const total = formatCurrency(order.total).toLowerCase();
        
        return clientName.includes(searchLower) || 
               orderNum.includes(searchLower) || 
               total.includes(searchLower);
      }
      
      return true;
    });
  }, [orders, customers, orderSearch, orderStatusFilter]);

  // Filter materials based on search
  const filteredMaterials = useMemo(() => {
    if (!materialSearch) return materials;
    const q = materialSearch.toLowerCase();
    return materials.filter(m =>
      m.name.toLowerCase().includes(q) ||
      m.sku?.toLowerCase().includes(q)
    );
  }, [materials, materialSearch]);

  // Filter invoices based on search and status
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (invoiceStatusFilter !== 'all' && inv.status !== invoiceStatusFilter) {
        return false;
      }
      if (invoiceSearch) {
        const q = invoiceSearch.toLowerCase();
        const invNum = `№${inv.invoice_number}`.toLowerCase();
        const amount = `${inv.amount}`.toLowerCase();
        const customer = (inv.customer_name || '').toLowerCase();
        const desc = (inv.description || '').toLowerCase();
        return invNum.includes(q) || amount.includes(q) || customer.includes(q) || desc.includes(q);
      }
      return true;
    });
  }, [invoices, invoiceSearch, invoiceStatusFilter]);

  useEffect(() => {
    loadData();
  }, [statusFilter, priorityFilter, dateFilter]);

  // Handle view parameter to open task details sheet from calendar
  useEffect(() => {
    const viewId = searchParams.get('view') || searchParams.get('open');
    if (viewId && !loading && tasks.length > 0) {
      const task = tasks.find(t => t.id === viewId);
      if (task) {
        setViewingTask(task);
      }
      searchParams.delete('view');
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [tasks, loading, searchParams, setSearchParams]);

  // Handle create parameter to auto-open create dialog from dashboard
  useEffect(() => {
    const shouldCreate = searchParams.get('create');
    if (shouldCreate === 'true' && !loading) {
      openDialog();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, searchParams, setSearchParams]);

  const loadData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;

      const [tasksRes, ordersRes, customersRes, materialsRes, invoicesRes] = await Promise.all([
        tasksAPI.getAll(params),
        ordersAPI.getAll({ limit: 1000 }),
        customersAPI.getAll({ limit: 1000 }),
        materialsAPI.getAll({ limit: 1000 }),
        invoicesAPI.getAll({ limit: 1000 }),
      ]);
      setTasks(tasksRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setMaterials(materialsRes.data);
      setInvoices(invoicesRes.data);
      
      // Load reminder settings
      try {
        const settingsRes = await settingsAPI.getNotifications();
        if (settingsRes.data.reminder_minutes) {
          setReminderMinutes(settingsRes.data.reminder_minutes);
        }
      } catch (e) { /* ignore - use default */ }
    } catch (error) {
      toast.error('Ошибка загрузки задач');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      // Parse due_date: handle both YYYY-MM-DD and old ISO formats
      let dueTime = '';
      let dueDate = null;
      if (task.due_date) {
        const str = String(task.due_date);
        if (str.length === 10 && str.includes('-') && !str.includes('T')) {
          // YYYY-MM-DD format
          const [y, m, d] = str.split('-').map(Number);
          dueDate = new Date(y, m - 1, d);
        } else if (str.includes('T')) {
          // Has time component
          if (str.endsWith('Z') || str.includes('+')) {
            // Old UTC ISO format — fix timezone shift
            const dt = new Date(str);
            if (dt.getUTCHours() >= 12 && dt.getUTCHours() < 24) {
              // Likely next day in local timezone
              const next = new Date(dt);
              next.setUTCDate(next.getUTCDate() + 1);
              dueDate = new Date(next.getUTCFullYear(), next.getUTCMonth(), next.getUTCDate());
            } else {
              dueDate = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
              if (dt.getUTCHours() !== 0 || dt.getUTCMinutes() !== 0) {
                dueTime = `${String(dt.getUTCHours()).padStart(2,'0')}:${String(dt.getUTCMinutes()).padStart(2,'0')}`;
              }
            }
          } else {
            // Local format YYYY-MM-DDTHH:MM:SS
            const [datePart, timePart] = str.split('T');
            const [y, m, d] = datePart.split('-').map(Number);
            dueDate = new Date(y, m - 1, d);
            if (timePart && timePart !== '00:00:00') {
              dueTime = timePart.substring(0, 5);
            }
          }
        } else {
          dueDate = new Date(str);
        }
      }
      setFormData({
        title: task.title,
        description: task.description || '',
        order_id: task.order_id || '',
        material_id: task.material_id || '',
        invoice_id: task.invoice_id || '',
        due_date: dueDate,
        due_time: dueTime,
        notify: task.notify !== false,
        priority: task.priority,
        status: task.status,
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        order_id: '',
        material_id: '',
        invoice_id: '',
        due_date: null,
        due_time: '',
        notify: true,
        priority: 'medium',
        status: 'pending',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Введите название задачи');
      return;
    }

    setSaving(true);
    try {
      // Build date string: use YYYY-MM-DD format (timezone-safe)
      let dueDateStr = null;
      if (formData.due_date) {
        const d = new Date(formData.due_date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        if (formData.due_time) {
          // Save date+time in local format: YYYY-MM-DDTHH:MM:SS
          dueDateStr = `${yyyy}-${mm}-${dd}T${formData.due_time}:00`;
        } else {
          // Date only
          dueDateStr = `${yyyy}-${mm}-${dd}`;
        }
      }

      const data = {
        ...formData,
        order_id: formData.order_id || null,
        material_id: formData.material_id || null,
        invoice_id: formData.invoice_id || null,
        due_date: dueDateStr,
        notify: formData.notify,
      };
      delete data.due_time; // not stored separately

      if (editingTask) {
        await tasksAPI.update(editingTask.id, data);
        toast.success('Задача обновлена');
      } else {
        await tasksAPI.create(data);
        toast.success('Задача создана');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      if (error.response?.status === 403 && !editingTask) {
        setDialogOpen(false);
        setPaywallOpen(true);
      } else {
        toast.error('Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.update(taskId, { status: newStatus });
      toast.success('Статус обновлён');
      loadData();
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const handleDelete = async (id) => {
    const task = tasks.find(t => t.id === id);
    const taskTitle = task?.title || 'эту задачу';
    if (!window.confirm(`Удалить задачу "${taskTitle}"?\n\nЭто действие нельзя отменить.`)) return;
    try {
      await tasksAPI.delete(id);
      toast.success('Задача удалена');
      loadData();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  // Helper: parse due_date correctly for both YYYY-MM-DD and old ISO formats
  const parseDueDate = (dueDate) => {
    if (!dueDate) return null;
    const str = String(dueDate);
    if (str.length === 10 && str.includes('-') && !str.includes('T')) {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    if (str.includes('T') && !str.endsWith('Z') && !str.includes('+')) {
      // Local format YYYY-MM-DDTHH:MM:SS
      const [y, m, d] = str.substring(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    // Old UTC ISO — apply timezone heuristic
    const dt = new Date(str);
    if (dt.getUTCHours() >= 12) {
      const next = new Date(dt);
      next.setUTCDate(next.getUTCDate() + 1);
      return new Date(next.getUTCFullYear(), next.getUTCMonth(), next.getUTCDate());
    }
    return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
  };

  // Helper function to check if date is in range
  const isDateInRange = (dueDate, filter) => {
    if (!dueDate) return filter === 'all' || filter === 'no_date';
    
    const taskDate = parseDueDate(dueDate);
    taskDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    switch (filter) {
      case 'yesterday':
        return taskDate.getTime() === yesterday.getTime();
      case 'today':
        return taskDate.getTime() === today.getTime();
      case 'tomorrow':
        return taskDate.getTime() === tomorrow.getTime();
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return taskDate >= weekStart && taskDate <= weekEnd;
      case 'this_month':
        return taskDate.getMonth() === today.getMonth() && 
               taskDate.getFullYear() === today.getFullYear();
      case 'next_month':
        const nextMonth = new Date(today);
        nextMonth.setMonth(today.getMonth() + 1);
        return taskDate.getMonth() === nextMonth.getMonth() && 
               taskDate.getFullYear() === nextMonth.getFullYear();
      case 'no_date':
        return false;
      case 'all':
      default:
        return true;
    }
  };

  // Filter tasks based on all filters
  const filteredTasks = tasks.filter(task => {
    // Status filter
    if (statusFilter !== 'all' && task.status !== statusFilter) {
      return false;
    }
    
    // Priority filter
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) {
      return false;
    }
    
    // Date filter
    if (!isDateInRange(task.due_date, dateFilter)) {
      return false;
    }
    
    return true;
  });

  // Sort filtered tasks by due_date
  const sortedTasks = sortByDate === 'none' ? filteredTasks : [...filteredTasks].sort((a, b) => {
    const dateA = a.due_date ? new Date(a.due_date).getTime() : null;
    const dateB = b.due_date ? new Date(b.due_date).getTime() : null;
    if (dateA === null && dateB === null) return 0;
    if (dateA === null) return 1;
    if (dateB === null) return -1;
    return sortByDate === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Group tasks by status
  const tasksByStatus = {
    pending: sortedTasks.filter(t => t.status === 'pending'),
    in_progress: sortedTasks.filter(t => t.status === 'in_progress'),
    completed: sortedTasks.filter(t => t.status === 'completed'),
  };

  return (
    <div className="flex flex-col gap-2" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Задачи</h1>
          <p className="text-sm text-muted-foreground">Управление задачами</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="w-full sm:w-auto" onClick={() => openDialog()} data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            Новая задача
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-2">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {Object.entries(TASK_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="priority-filter">
                <SelectValue placeholder="Приоритет" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все приоритеты</SelectItem>
                {Object.entries(TASK_PRIORITIES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="date-filter">
                <SelectValue placeholder="Дата" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все даты</SelectItem>
                <SelectItem value="yesterday">Вчера</SelectItem>
                <SelectItem value="today">Сегодня</SelectItem>
                <SelectItem value="tomorrow">Завтра</SelectItem>
                <SelectItem value="this_week">Эта неделя</SelectItem>
                <SelectItem value="this_month">Этот месяц</SelectItem>
                <SelectItem value="next_month">Следующий месяц</SelectItem>
                <SelectItem value="no_date">Без даты</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortByDate} onValueChange={setSortByDate}>
              <SelectTrigger className="w-full sm:w-48" data-testid="sort-by-date">
                <SelectValue placeholder="Сортировка" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Без сортировки</SelectItem>
                <SelectItem value="asc">Сначала ближайшие</SelectItem>
                <SelectItem value="desc">Сначала дальние</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-96" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <CheckSquare className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-semibold">Нет задач</h3>
            <p className="text-muted-foreground mt-2 mb-1">
              Создайте первую задачу
            </p>
            <p className="text-xs text-muted-foreground/60 mb-4">YaDela.ru — твои дела проще</p>
            <Button onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Новая задача
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(TASK_STATUSES).map(([statusKey, { label, color }]) => (
            <div key={statusKey} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <h3 className="font-semibold">{label}</h3>
                </div>
                <Badge variant="secondary">{tasksByStatus[statusKey].length}</Badge>
              </div>
              
              <div>
                {tasksByStatus[statusKey].map((task, index, array) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    orders={orders}
                    customers={customers}
                    materials={materials}
                    invoices={invoices}
                    onView={() => setViewingTask(task)}
                    onEdit={() => openDialog(task)}
                    onDelete={() => handleDelete(task.id)}
                    onStatusChange={handleStatusChange}
                    className={index < array.length - 1 ? "mb-5" : ""}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingTask ? 'Редактировать задачу' : 'Новая задача'}
            </DialogTitle>
            <DialogDescription>
              Заполните информацию о задаче
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 min-w-0 overflow-hidden">
            <div className="space-y-2">
              <Label>Название *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Название задачи"
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Описание задачи"
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Приоритет</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_PRIORITIES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Статус</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_STATUSES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Срок выполнения</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.due_date && "text-muted-foreground"
                    )}
                    data-testid="task-due-date-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    {formData.due_date
                      ? format(formData.due_date, 'PPP', { locale: ru })
                      : 'Выберите дату'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.due_date}
                    onSelect={(date) => setFormData({ ...formData, due_date: date, notify: false, due_time: '' })}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notify toggle — only when date is set */}
            {formData.due_date && (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium cursor-pointer" htmlFor="task-notify">
                      🔔 Уведомление
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {formData.notify ? 'Напомнить в указанное время' : 'Без уведомления'}
                    </p>
                  </div>
                  <Switch
                    id="task-notify"
                    checked={formData.notify}
                    onCheckedChange={(v) => setFormData({ ...formData, notify: v, due_time: v ? formData.due_time : '' })}
                  />
                </div>

                {/* Time input — only when notify is ON */}
                {formData.notify && (
                  <div>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                      <input
                        type="time"
                        value={formData.due_time}
                        onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-8 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                      />
                    </div>
                    {formData.due_time && (
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Уведомление за {reminderMinutes >= 60 ? `${Math.floor(reminderMinutes / 60)} ч${reminderMinutes % 60 > 0 ? ` ${reminderMinutes % 60} мин` : ''}` : `${reminderMinutes} мин`} — в {(() => {
                          const [h, m] = formData.due_time.split(':').map(Number);
                          const total = h * 60 + m - reminderMinutes;
                          const rh = Math.floor(((total % 1440) + 1440) % 1440 / 60);
                          const rm = ((total % 1440) + 1440) % 1440 % 60;
                          return `${String(rh).padStart(2,'0')}:${String(rm).padStart(2,'0')}`;
                        })()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Связанный заказ</Label>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between overflow-hidden"
                onClick={() => setOrderSelectOpen(!orderSelectOpen)}
                data-testid="task-order-select"
              >
                <span className="truncate">
                {formData.order_id ? (
                  (() => {
                    const selectedOrder = orders.find(o => o.id === formData.order_id);
                    if (!selectedOrder) return 'Выберите заказ';
                    const clientName = getClientNameFromOrder(selectedOrder);
                    return `${selectedOrder.order_number_display || '№' + selectedOrder.order_number}${clientName ? ` • ${clientName}` : ''} • ${formatCurrency(selectedOrder.total)}`;
                  })()
                ) : (
                  'Выберите заказ'
                )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {orderSelectOpen && (
                <div ref={orderPanelRef} className="rounded-md border bg-popover shadow-sm w-full overflow-hidden">
                  <div className="p-3 border-b space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Поиск по имени, номеру..."
                        value={orderSearch}
                        onChange={(e) => setOrderSearch(e.target.value)}
                        className="pl-9"
                        data-testid="order-search-input"
                      />
                      {orderSearch && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          onClick={() => setOrderSearch('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={orderStatusFilter === 'active' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setOrderStatusFilter('active')}
                        className="flex-1"
                        data-testid="order-filter-active"
                      >
                        Активные
                      </Button>
                      <Button
                        variant={orderStatusFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setOrderStatusFilter('all')}
                        className="flex-1"
                        data-testid="order-filter-all"
                      >
                        Все заказы
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto overscroll-contain p-2">
                    <div
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                        !formData.order_id && "bg-muted"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, order_id: '' });
                        setOrderSelectOpen(false);
                      }}
                      data-testid="order-option-none"
                    >
                      <Check className={cn("h-4 w-4", !formData.order_id ? "opacity-100" : "opacity-0")} />
                      <span className="text-muted-foreground">Без заказа</span>
                    </div>
                    
                    {filteredOrders.length === 0 ? (
                      <div className="px-3 py-6 text-center text-muted-foreground text-sm">
                        {orderSearch ? 'Заказы не найдены' : 'Нет активных заказов'}
                      </div>
                    ) : (
                      filteredOrders.map((order) => {
                        const clientName = getClientNameFromOrder(order);
                        const isSelected = formData.order_id === order.id;
                        return (
                          <div
                            key={order.id}
                            className={cn(
                              "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                              isSelected && "bg-muted"
                            )}
                            onClick={() => {
                              setFormData({ ...formData, order_id: order.id });
                              setOrderSelectOpen(false);
                              setOrderSearch('');
                            }}
                            data-testid={`order-option-${order.order_number}`}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                                <span className="font-medium shrink-0">{order.order_number_display || `№${order.order_number}`}</span>
                                {clientName && (
                                  <span className="truncate text-sm">{clientName}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mt-0.5">
                                <span>{formatCurrency(order.total)}</span>
                                <Badge variant="outline" className="text-xs">
                                  {ORDER_STATUSES[order.status]?.label || order.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {orders.length > 0 && (
                    <div className="p-2 border-t text-xs text-muted-foreground text-center">
                      Показано {filteredOrders.length} из {orders.length} заказов
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Material selector */}
            <div className="space-y-2">
              <Label>Связанный материал</Label>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between overflow-hidden"
                onClick={() => setMaterialSelectOpen(!materialSelectOpen)}
                data-testid="task-material-select"
              >
                <span className="truncate">
                {formData.material_id ? (
                  (() => {
                    const mat = materials.find(m => m.id === formData.material_id);
                    if (!mat) return 'Выберите материал';
                    return `${mat.name}${mat.sku ? ` (${mat.sku})` : ''} — ${mat.quantity} ${mat.unit}`;
                  })()
                ) : (
                  'Выберите материал'
                )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {materialSelectOpen && (
                <div ref={materialPanelRef} className="rounded-md border bg-popover shadow-sm w-full overflow-hidden">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Поиск по названию, артикулу..."
                        value={materialSearch}
                        onChange={(e) => setMaterialSearch(e.target.value)}
                        className="pl-9"
                        data-testid="material-search-input"
                      />
                      {materialSearch && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          onClick={() => setMaterialSearch('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto overscroll-contain p-2">
                    <div
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                        !formData.material_id && "bg-muted"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, material_id: '' });
                        setMaterialSelectOpen(false);
                      }}
                      data-testid="material-option-none"
                    >
                      <Check className={cn("h-4 w-4", !formData.material_id ? "opacity-100" : "opacity-0")} />
                      <span className="text-muted-foreground">Без материала</span>
                    </div>

                    {filteredMaterials.length === 0 ? (
                      <div className="px-3 py-6 text-center text-muted-foreground text-sm">
                        {materialSearch ? 'Материалы не найдены' : 'Нет материалов'}
                      </div>
                    ) : (
                      filteredMaterials.map((mat) => {
                        const isSelected = formData.material_id === mat.id;
                        const isLow = mat.quantity <= mat.min_quantity;
                        return (
                          <div
                            key={mat.id}
                            className={cn(
                              "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                              isSelected && "bg-muted"
                            )}
                            onClick={() => {
                              setFormData({ ...formData, material_id: mat.id });
                              setMaterialSelectOpen(false);
                              setMaterialSearch('');
                            }}
                            data-testid={`material-option-${mat.id}`}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                            <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium truncate">{mat.name}</span>
                                {mat.sku && <span className="text-xs text-muted-foreground truncate">{mat.sku}</span>}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                <span className={isLow ? 'text-destructive font-medium' : ''}>{mat.quantity} {mat.unit}</span>
                                {isLow && <Badge variant="destructive" className="text-[10px] px-1 py-0">Мало</Badge>}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {materials.length > 0 && (
                    <div className="p-2 border-t text-xs text-muted-foreground text-center">
                      Показано {filteredMaterials.length} из {materials.length} материалов
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Invoice selector */}
            <div className="space-y-2">
              <Label>Связанный счёт</Label>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between overflow-hidden"
                onClick={() => setInvoiceSelectOpen(!invoiceSelectOpen)}
                data-testid="task-invoice-select"
              >
                <span className="truncate">
                {formData.invoice_id ? (
                  (() => {
                    const inv = invoices.find(i => i.id === formData.invoice_id);
                    if (!inv) return 'Выберите счёт';
                    return `Счёт №${inv.invoice_number} • ${formatCurrency(inv.amount)}${inv.customer_name ? ` • ${inv.customer_name}` : ''}`;
                  })()
                ) : (
                  'Выберите счёт'
                )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
              {invoiceSelectOpen && (
                <div ref={invoicePanelRef} className="rounded-md border bg-popover shadow-sm w-full overflow-hidden">
                  <div className="p-3 border-b space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Поиск по номеру, сумме, клиенту..."
                        value={invoiceSearch}
                        onChange={(e) => setInvoiceSearch(e.target.value)}
                        className="pl-9"
                        data-testid="invoice-search-input"
                      />
                      {invoiceSearch && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          onClick={() => setInvoiceSearch('')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={invoiceStatusFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setInvoiceStatusFilter('all')}
                        className="flex-1"
                      >
                        Все
                      </Button>
                      <Button
                        variant={invoiceStatusFilter === 'new' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setInvoiceStatusFilter('new')}
                        className="flex-1"
                      >
                        Новые
                      </Button>
                      <Button
                        variant={invoiceStatusFilter === 'paid' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setInvoiceStatusFilter('paid')}
                        className="flex-1"
                      >
                        Оплаченные
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto overscroll-contain p-2">
                    <div
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                        !formData.invoice_id && "bg-muted"
                      )}
                      onClick={() => {
                        setFormData({ ...formData, invoice_id: '' });
                        setInvoiceSelectOpen(false);
                      }}
                      data-testid="invoice-option-none"
                    >
                      <Check className={cn("h-4 w-4", !formData.invoice_id ? "opacity-100" : "opacity-0")} />
                      <span className="text-muted-foreground">Без счёта</span>
                    </div>

                    {filteredInvoices.length === 0 ? (
                      <div className="px-3 py-6 text-center text-muted-foreground text-sm">
                        {invoiceSearch ? 'Счета не найдены' : 'Нет счетов'}
                      </div>
                    ) : (
                      filteredInvoices.map((inv) => {
                        const isSelected = formData.invoice_id === inv.id;
                        const statusLabels = { new: 'Новый', paid: 'Оплачен', cancelled: 'Отменён' };
                        const statusColors = { new: 'text-blue-700 bg-blue-50 border-blue-200', paid: 'text-emerald-700 bg-emerald-50 border-emerald-200', cancelled: 'text-red-700 bg-red-50 border-red-200' };
                        return (
                          <div
                            key={inv.id}
                            className={cn(
                              "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-muted",
                              isSelected && "bg-muted"
                            )}
                            onClick={() => {
                              setFormData({ ...formData, invoice_id: inv.id });
                              setInvoiceSelectOpen(false);
                              setInvoiceSearch('');
                            }}
                            data-testid={`invoice-option-${inv.invoice_number}`}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium shrink-0">Счёт №{inv.invoice_number}</span>
                                {inv.customer_name && (
                                  <span className="truncate text-sm text-muted-foreground">{inv.customer_name}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                <span className="font-medium">{formatCurrency(inv.amount)}</span>
                                <Badge variant="outline" className={cn("text-xs border", statusColors[inv.status] || '')}>
                                  {statusLabels[inv.status] || inv.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {invoices.length > 0 && (
                    <div className="p-2 border-t text-xs text-muted-foreground text-center">
                      Показано {filteredInvoices.length} из {invoices.length} счетов
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-between gap-2 pt-2">
            <Button variant="outline" size="lg" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button size="lg" onClick={handleSave} disabled={saving} data-testid="save-task-btn">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Sheet */}
      {viewingTask && (
        <TaskDetailSheet
          task={viewingTask}
          orders={orders}
          customers={customers}
          materials={materials}
          invoices={invoices}
          onClose={() => setViewingTask(null)}
          onEdit={() => {
            setViewingTask(null);
            openDialog(viewingTask);
          }}
        />
      )}

      <PaywallDialog
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        resourceType="tasks"
        limit={3}
      />
    </div>
  );
}

// Task Detail Sheet Component
function TaskDetailSheet({ task, orders, customers, materials, invoices, onClose, onEdit }) {
  const navigate = useNavigate();
  
  if (!task) return null;

  const order = orders.find(o => o.id === task.order_id);
  const material = materials?.find(m => m.id === task.material_id);
  const invoice = invoices?.find(i => i.id === task.invoice_id);
  const customer = order?.customer_id ? customers.find(c => c.id === order.customer_id) : null;
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  // Get client name
  const getClientName = () => {
    if (!order) return null;
    if (customer?.name) return customer.name;
    if (order.data) {
      const nameFields = ['name', 'client_name', 'customer_name', 'имя', 'клиент', 'ФИО'];
      for (const field of nameFields) {
        if (order.data[field]) return order.data[field];
      }
    }
    return null;
  };

  const clientName = getClientName();
  const statusInfo = TASK_STATUSES[task.status] || {};
  const priorityInfo = TASK_PRIORITIES[task.priority] || {};

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
        {/* Header */}
        <div className={cn(
          "px-6 pt-8 pb-6",
          task.status === 'completed' 
            ? "bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent"
            : isOverdue 
              ? "bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent"
              : "bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
        )}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-4 mt-2">
            <div className={cn(
              "h-14 w-14 rounded-xl flex items-center justify-center border-2 border-background shadow-sm",
              task.status === 'completed' 
                ? "bg-green-500/20 text-green-600"
                : isOverdue 
                  ? "bg-red-500/20 text-red-600"
                  : "bg-primary/20 text-primary"
            )}>
              <CheckSquare className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-lg font-bold leading-tight">{task.title}</h2>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={task.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                  {statusInfo.label || task.status}
                </Badge>
                <Badge variant={task.priority === 'high' ? 'destructive' : 'outline'} className="text-xs">
                  {priorityInfo.label || task.priority}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pt-6 pb-8">
          {/* Due Date Card */}
          {task.due_date && (
            <div className={cn(
              "relative overflow-hidden rounded-xl border p-4 mb-6",
              isOverdue && task.status !== 'completed'
                ? "bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20"
                : "bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20"
            )}>
              <Clock className={cn(
                "absolute -right-2 -bottom-2 h-12 w-12",
                isOverdue && task.status !== 'completed' ? "text-red-500/10" : "text-blue-500/10"
              )} />
              <div className="relative">
                <p className={cn(
                  "text-xs font-medium uppercase tracking-wide",
                  isOverdue && task.status !== 'completed' ? "text-red-600 dark:text-red-400" : "text-blue-600 dark:text-blue-400"
                )}>
                  {isOverdue && task.status !== 'completed' ? 'Просрочено ⚠️' : 'Срок выполнения'}
                </p>
                <p className="text-xl font-bold mt-1">{formatDate(task.due_date)}</p>
              </div>
            </div>
          )}

          {/* Description */}
          {task.description && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                Описание
              </h4>
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{task.description}</p>
              </div>
            </div>
          )}

          {/* Order Info */}
          {order && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                Связанный заказ
              </h4>
              <div 
                className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                onClick={() => {
                  onClose();
                  navigate(`/orders?view=${order.id}`);
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-primary">Заказ {order.order_number_display || `№${order.order_number}`} →</p>
                    {clientName && <p className="text-sm text-muted-foreground">{clientName}</p>}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {ORDER_STATUSES[order.status]?.label || order.status}
                  </Badge>
                </div>
                {order.total_price > 0 && (
                  <p className="text-lg font-bold mt-2">{formatCurrency(order.total_price)}</p>
                )}
                <p className="text-xs text-muted-foreground mt-2">Нажмите для просмотра заказа</p>
              </div>
            </div>
          )}

          {/* Material Info */}
          {material && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                Связанный материал
              </h4>
              <div 
                className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                onClick={() => {
                  onClose();
                  navigate(`/materials?view=${material.id}`);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{material.name}</p>
                      {material.sku && <p className="text-xs text-muted-foreground">{material.sku}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn("text-sm font-semibold", material.quantity <= material.min_quantity && "text-destructive")}>
                      {material.quantity} {material.unit}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(material.price)}/{material.unit}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Нажмите для перехода к материалам</p>
              </div>
            </div>
          )}

          {/* Invoice Info */}
          {invoice && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                Связанный счёт
              </h4>
              <div 
                className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                onClick={() => {
                  onClose();
                  navigate('/invoices');
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">Счёт №{invoice.invoice_number} →</p>
                      {invoice.customer_name && <p className="text-xs text-muted-foreground">{invoice.customer_name}</p>}
                    </div>
                  </div>
                  <Badge variant="outline" className={cn("text-xs border", 
                    invoice.status === 'paid' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 
                    invoice.status === 'cancelled' ? 'text-red-700 bg-red-50 border-red-200' :
                    'text-blue-700 bg-blue-50 border-blue-200'
                  )}>
                    {invoice.status === 'paid' ? 'Оплачен' : invoice.status === 'cancelled' ? 'Отменён' : 'Новый'}
                  </Badge>
                </div>
                <p className="text-lg font-bold mt-2">{formatCurrency(invoice.amount)}</p>
                <p className="text-xs text-muted-foreground mt-2">Нажмите для перехода к счетам</p>
              </div>
            </div>
          )}

          {/* Customer Info */}
          {customer && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                Клиент
              </h4>
              <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => {
                    onClose();
                    navigate(`/customers?view=${customer.id}`);
                  }}
                >
                  <p className="font-medium text-primary">{customer.name} →</p>
                  <p className="text-xs text-muted-foreground mt-1">Нажмите для перехода к клиенту</p>
                </div>
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors" onClick={(e) => e.stopPropagation()}>
                    <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Phone className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm">{customer.phone}</span>
                  </a>
                )}
                {customer.email && (
                  <a href={`mailto:${customer.email}`} className="flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors" onClick={(e) => e.stopPropagation()}>
                    <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm">{customer.email}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8">
            <Button 
              onClick={onEdit} 
              className="w-full h-12 font-medium text-base"
              variant="default"
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать задачу
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TaskCard({ task, orders, customers, materials, invoices, onView, onEdit, onDelete, onStatusChange, className }) {
  const navigate = useNavigate();
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const order = orders.find(o => o.id === task.order_id);
  const material = materials?.find(m => m.id === task.material_id);
  const invoice = invoices?.find(i => i.id === task.invoice_id);
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
  
  // Get customer from order
  const customer = order?.customer_id ? customers.find(c => c.id === order.customer_id) : null;
  
  // Get client name - first from customer card, then from order data
  const getClientName = () => {
    if (!order) return null;
    if (customer?.name) return customer.name;
    if (order.data) {
      const nameFields = ['name', 'client_name', 'customer_name', 'имя', 'клиент', 'ФИО'];
      for (const field of nameFields) {
        if (order.data[field]) return order.data[field];
      }
    }
    return null;
  };
  
  const clientName = getClientName();
  
  const handleCustomerClick = (e) => {
    e.stopPropagation();
    if (customer?.id) {
      setCustomerDialogOpen(true);
    }
  };

  const priorityColors = {
    high: 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/40 dark:border-red-800',
    medium: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-950/40 dark:border-amber-800',
    low: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/40 dark:border-emerald-800',
  };

  const statusBadgeColors = {
    pending: 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
    in_progress: 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/60 dark:text-amber-300',
    completed: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/60 dark:text-emerald-300',
  };

  return (
    <>
      <Card 
        className={cn(
          "hover:shadow-md transition-all",
          isOverdue && "border-destructive/50",
          className
        )}
        data-testid={`task-card-${task.id}`}
      >
        <CardContent className="p-4">
          {/* Row 1: Title + Status dropdown */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-sm leading-tight">{task.title}</h4>
                <Badge 
                  variant="outline"
                  className={cn("text-[10px] px-1.5 py-0 border shrink-0", priorityColors[task.priority])}
                >
                  {TASK_PRIORITIES[task.priority]?.label}
                </Badge>
                {isOverdue && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 border border-destructive/30 px-2 py-0.5 text-[10px] font-bold text-destructive leading-none shrink-0">
                    <Clock className="h-2.5 w-2.5" />
                    Просрочено
                  </span>
                )}
              </div>
            </div>
            <Select 
              value={task.status} 
              onValueChange={(value) => onStatusChange(task.id, value)}
            >
              <SelectTrigger className="h-auto w-auto border-0 p-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:hidden shrink-0">
                <Badge className={cn("cursor-pointer transition-opacity hover:opacity-80 text-xs", statusBadgeColors[task.status])}>
                  {TASK_STATUSES[task.status]?.label}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Badge>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TASK_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>
                    <Badge className={cn("text-xs", statusBadgeColors[key])}>{label}</Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Description */}
          {task.description && (
            <p className="text-sm font-medium line-clamp-2 mb-2">
              {task.description}
            </p>
          )}

          {/* Row 3: Customer (from linked order) */}
          {order && clientName && (
            <div className="flex items-center justify-between mb-2" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                <span className="font-mono shrink-0">{order.order_number_display || `№${order.order_number}`}</span>
                <span>•</span>
                {customer ? (
                  <button 
                    onClick={handleCustomerClick}
                    className="text-primary hover:underline underline-offset-2 truncate max-w-[60%] text-left text-xs"
                    title="Открыть карточку клиента"
                  >
                    {clientName}
                  </button>
                ) : (
                  <span className="truncate text-xs">{clientName}</span>
                )}
              </div>
              {customer && <CustomerSocialIcons customer={customer} />}
            </div>
          )}
          {order && !clientName && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <span className="font-mono">{order.order_number_display || `№${order.order_number}`}</span>
              <span>•</span>
              <span>Без клиента</span>
            </div>
          )}

          {/* Row 4: Linked entities */}
          {(material || invoice) && (
            <div className="text-xs text-muted-foreground space-y-1 mb-2">
              {material && (
                <div className="flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium">{material.name}</span>
                  <span className={material.quantity <= material.min_quantity ? 'text-destructive font-medium' : ''}>
                    {material.quantity} {material.unit}
                  </span>
                </div>
              )}
              {invoice && (
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium">Счёт №{invoice.invoice_number}</span>
                  <span>{formatCurrency(invoice.amount)}</span>
                  <Badge variant="outline" className={cn("text-[10px] px-1 py-0 border",
                    invoice.status === 'paid' ? 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/40' :
                    invoice.status === 'cancelled' ? 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/40' :
                    'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-950/40'
                  )}>
                    {invoice.status === 'paid' ? 'Оплачен' : invoice.status === 'cancelled' ? 'Отменён' : 'Новый'}
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* Footer: Date + Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-border/50">
            <div className="flex items-center">
              {task.due_date ? (
                <div className={cn(
                  "flex items-center gap-1.5 text-xs",
                  isOverdue ? "text-destructive font-medium" : "text-muted-foreground"
                )}>
                  <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                  <span>Срок: {task.due_date.includes('T') ? formatDateTime(task.due_date) : formatDate(task.due_date)}</span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">Без срока</span>
              )}
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onView}
                title="Просмотреть задачу"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onEdit}
                title="Редактировать задачу"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                onClick={onDelete}
                title="Удалить задачу"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

    {/* Customer Details Dialog */}
    {customer && (
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto px-4 sm:px-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                {customer.name.charAt(0).toUpperCase()}
              </div>
              {customer.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-3 sm:py-2">
            {/* Contact Info */}
            {(customer.phone || customer.email || customer.address) && (
              <Card className="shadow-sm mb-4 sm:mb-5">
                <CardContent className="p-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-3">Контакты</h4>
                  <div className="space-y-1">
                    {customer.phone && (
                      <a 
                        href={`tel:${customer.phone}`} 
                        className="flex items-center gap-3 p-2.5 -mx-1 rounded-md hover:bg-muted/50 transition-colors group"
                      >
                        <Phone className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                        <span className="text-sm flex-1">{customer.phone}</span>
                      </a>
                    )}
                    {customer.email && (
                      <a 
                        href={`mailto:${customer.email}`} 
                        className="flex items-center gap-3 p-2.5 -mx-1 rounded-md hover:bg-muted/50 transition-colors group"
                      >
                        <Mail className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                        <span className="text-sm flex-1 truncate">{customer.email}</span>
                      </a>
                    )}
                    {customer.address && (
                      <div className="flex items-start gap-3 p-2.5 -mx-1 rounded-md">
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm flex-1 leading-relaxed">{customer.address}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social Links */}
            {customer.social_links && Object.values(customer.social_links).some(v => v) && (
              <Card className="shadow-sm mb-4 sm:mb-5">
                <CardContent className="p-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-3">Социальные сети</h4>
                  <div className="flex flex-wrap gap-2">
                    {customer.social_links.telegram && (
                      <a
                        href={`https://t.me/${customer.social_links.telegram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0088cc]/10 hover:bg-[#0088cc]/20 transition-colors text-xs font-medium"
                      >
                        <SocialIcon type="telegram" className="h-3.5 w-3.5 text-[#0088cc]" />
                        <span className="text-[#0088cc]">Telegram</span>
                      </a>
                    )}
                    {customer.social_links.whatsapp && (
                      <a
                        href={`https://wa.me/${customer.social_links.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors text-xs font-medium"
                      >
                        <SocialIcon type="whatsapp" className="h-3.5 w-3.5 text-[#25D366]" />
                        <span className="text-[#25D366]">WhatsApp</span>
                      </a>
                    )}
                    {customer.social_links.instagram && (
                      <a
                        href={`https://instagram.com/${customer.social_links.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#E4405F]/10 hover:bg-[#E4405F]/20 transition-colors text-xs font-medium"
                      >
                        <SocialIcon type="instagram" className="h-3.5 w-3.5 text-[#E4405F]" />
                        <span className="text-[#E4405F]">Instagram</span>
                      </a>
                    )}
                    {customer.social_links.vk && (
                      <a
                        href={`https://vk.com/${customer.social_links.vk}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0077FF]/10 hover:bg-[#0077FF]/20 transition-colors text-xs font-medium"
                      >
                        <SocialIcon type="vk" className="h-3.5 w-3.5 text-[#0077FF]" />
                        <span className="text-[#0077FF]">ВКонтакте</span>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats */}
            {(customer.total_orders > 0 || customer.total_spent > 0) && (
              <Card className="shadow-sm mb-4 sm:mb-5">
                <CardContent className="p-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-3">Статистика</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                      <div className="text-xs text-muted-foreground mb-1.5">Заказов</div>
                      <div className="text-xl font-semibold">{customer.total_orders || 0}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                      <div className="text-xs text-muted-foreground mb-1.5">Всего на</div>
                      <div className="text-xl font-semibold">{formatCurrency(customer.total_spent || 0)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {customer.notes && (
              <Card className="shadow-sm">
                <CardContent className="p-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-3">Заметки</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {customer.notes}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setCustomerDialogOpen(false)}
              className="flex-1 order-2 sm:order-1"
            >
              Закрыть
            </Button>
            <Button
              onClick={() => {
                setCustomerDialogOpen(false);
                navigate(`/customers?highlight=${customer.id}`);
              }}
              className="flex-1 order-1 sm:order-2"
            >
              Открыть карточку
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
